// Hover icons + portal popovers for race configuration JSON and athlete assignment CSV metadata.

import React, { JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileJson, Table2 } from 'lucide-react';
import { formatApiDateTime, formatUserIdentityDisplay } from '../../../helpers/helpers';
import { extractAthleteAssignmentsFileMeta } from '../utils/athleteAssignmentsMeta';
import { dispatchToast } from '../utils';
import LoadingState from '../../shared/LoadingState';

const POPOVER_Z_PANEL = 3810;
const PANEL_W = 320;
const LEAVE_MS = 180;

type RaceMetaState =
  | { status: 'idle' | 'loading' }
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      fileName?: string;
      created_at: number;
      created_by: string;
      edited_at: number;
      edited_by: string;
    };

type AthleteMetaState =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | {
      status: 'ready';
      fileId?: string;
      fileName?: string;
      /** Latest file (last upload) */
      createdAt?: number;
      createdBy?: string;
      createdByDisplayName?: string;
      /** First athlete-assignments file for the first race (from API) */
      firstUploadedAt?: number;
      firstUploadedBy?: string;
      firstUploadedByDisplayName?: string;
    };

function placeUnderAnchor(anchor: HTMLElement): { top: number; left: number } {
  const r = anchor.getBoundingClientRect();
  let left = r.right - PANEL_W;
  left = Math.max(8, Math.min(left, window.innerWidth - PANEL_W - 8));
  const top = Math.min(r.bottom + 6, window.innerHeight - 16);
  return { top, left };
}

type Props = {
  eventId: string;
  globalUserIdToDisplayName: Record<string, string>;
  /** Bump when event details refresh so metadata refetches. */
  refreshVersion?: number;
};

export function EventExpandedFileMetadataIcons(props: Props): JSX.Element {
  const { eventId, globalUserIdToDisplayName, refreshVersion = 0 } = props;

  const [race, setRace] = useState<RaceMetaState>({ status: 'idle' });
  const [athlete, setAthlete] = useState<AthleteMetaState>({ status: 'idle' });

  const raceBtnRef = useRef<HTMLButtonElement>(null);
  const athleteBtnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<'race' | 'athlete' | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const leaveTimerRef = useRef<number | undefined>(undefined);

  const clearLeave = useCallback(() => {
    if (leaveTimerRef.current !== undefined) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = undefined;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearLeave();
    leaveTimerRef.current = window.setTimeout(() => setOpen(null), LEAVE_MS);
  }, [clearLeave]);

  const openRace = useCallback(() => {
    clearLeave();
    setOpen('race');
  }, [clearLeave]);

  const openAthlete = useCallback(() => {
    clearLeave();
    setOpen('athlete');
  }, [clearLeave]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = open === 'race' ? raceBtnRef.current : athleteBtnRef.current;
    if (!el) return;
    setPos(placeUnderAnchor(el));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onScrollResize(): void {
      const el = open === 'race' ? raceBtnRef.current : athleteBtnRef.current;
      if (!el) return;
      setPos(placeUnderAnchor(el));
    }
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!eventId || !window.eventsAPI) return;

    let cancelled = false;
    setRace({ status: 'loading' });
    setAthlete({ status: 'loading' });

    void (async () => {
      let firstRaceId = '';
      try {
        const racesRes = await window.eventsAPI.listEventRaces(eventId);
        if (cancelled) return;
        const rr = racesRes as { ok?: boolean; data?: { races?: { raceId?: string }[] }; error?: string };
        if (!rr?.ok) {
          setRace({
            status: 'error',
            message: String(rr?.error || 'Failed to list races'),
          });
        } else {
          const races = Array.isArray(rr.data?.races) ? rr.data!.races! : [];
          firstRaceId = String(races[0]?.raceId ?? '').trim();
          if (!firstRaceId) {
            setRace({ status: 'empty' });
          } else {
            const res = await window.eventsAPI.getRaceConfiguration({
              eventId,
              raceId: firstRaceId,
            });
            if (cancelled) return;
            if (!res?.ok) {
              setRace({
                status: 'error',
                message: String((res as { error?: string })?.error || 'Failed to load race configuration'),
              });
            } else {
              const data = res.data as {
                file?: { fileName?: unknown };
                configuration?: Record<string, unknown>;
              } | null;
              const cfg = data?.configuration ?? {};
              const fileName =
                typeof data?.file?.fileName === 'string' ? data.file.fileName : undefined;
              setRace({
                status: 'ready',
                fileName,
                created_at: Number(cfg.created_at) || 0,
                created_by: typeof cfg.created_by === 'string' ? cfg.created_by : '',
                edited_at: Number(cfg.edited_at) || 0,
                edited_by: typeof cfg.edited_by === 'string' ? cfg.edited_by : '',
              });
            }
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setRace({
            status: 'error',
            message: e instanceof Error ? e.message : 'Failed to load race configuration',
          });
        }
      }

      try {
        if (!firstRaceId) {
          if (!cancelled) setAthlete({ status: 'empty' });
          return;
        }
        const res = await window.eventsAPI.getAthletesCsvMeta(eventId, { raceId: firstRaceId });
        if (cancelled) return;
        if (!res?.ok) {
          setAthlete({
            status: 'error',
            message: String((res as { error?: string })?.error || 'Failed to load athlete assignments file'),
          });
          return;
        }
        const meta = extractAthleteAssignmentsFileMeta(res);
        if (!meta) {
          setAthlete({ status: 'empty' });
          return;
        }
        const createdAt =
          typeof meta.createdAt === 'number'
            ? meta.createdAt
            : Number(meta.createdAt ?? meta.created_at ?? 0) || undefined;
        const firstUploadedAtRaw = meta.firstUploadedAt ?? meta.first_uploaded_at;
        const firstUploadedAtNum =
          typeof firstUploadedAtRaw === 'number'
            ? firstUploadedAtRaw
            : Number(firstUploadedAtRaw ?? 0) || undefined;
        const createdByStr =
          typeof meta.createdBy === 'string' ? meta.createdBy : undefined;
        const createdByDisplayStr =
          typeof meta.createdByDisplayName === 'string'
            ? meta.createdByDisplayName
            : typeof meta.created_by === 'string'
              ? meta.created_by
              : undefined;
        const firstByStr =
          typeof meta.firstUploadedBy === 'string'
            ? meta.firstUploadedBy
            : typeof meta.first_uploaded_by === 'string'
              ? meta.first_uploaded_by
              : createdByStr;
        const firstByDisplayStr =
          typeof meta.firstUploadedByDisplayName === 'string'
            ? meta.firstUploadedByDisplayName
            : typeof meta.first_uploaded_by_display_name === 'string'
              ? meta.first_uploaded_by_display_name
              : createdByDisplayStr;
        setAthlete({
          status: 'ready',
          fileId: typeof meta.fileId === 'string' ? meta.fileId : String(meta.fileId ?? ''),
          fileName: typeof meta.fileName === 'string' ? meta.fileName : undefined,
          createdAt,
          createdBy: createdByStr,
          createdByDisplayName: createdByDisplayStr,
          firstUploadedAt:
            firstUploadedAtNum != null && firstUploadedAtNum > 0
              ? firstUploadedAtNum
              : createdAt,
          firstUploadedBy: firstByStr,
          firstUploadedByDisplayName: firstByDisplayStr,
        });
      } catch (e: unknown) {
        if (!cancelled) {
          setAthlete({
            status: 'error',
            message: e instanceof Error ? e.message : 'Failed to load athlete assignments file',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId, refreshVersion]);

  const raceErrorMessage = race.status === 'error' ? race.message : null;
  const athleteErrorMessage = athlete.status === 'error' ? athlete.message : null;

  useEffect(() => {
    if (!raceErrorMessage) return;
    dispatchToast('toast-error', `Race configuration file: ${raceErrorMessage}`);
  }, [raceErrorMessage]);

  useEffect(() => {
    if (!athleteErrorMessage) return;
    dispatchToast('toast-error', `Athlete assignments file: ${athleteErrorMessage}`);
  }, [athleteErrorMessage]);

  useEffect(() => {
    return () => clearLeave();
  }, [clearLeave]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(null);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const panel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
              className="fixed max-h-[min(70vh,420px)] w-[320px] overflow-y-auto rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-left shadow-2xl ring-1 ring-black/5"
              style={{ zIndex: POPOVER_Z_PANEL, top: pos.top, left: pos.left, width: PANEL_W }}
              role="dialog"
              aria-label={open === 'race' ? 'Race configuration file' : 'Athlete assignments file'}
              onMouseEnter={clearLeave}
              onMouseLeave={scheduleClose}
            >
              {open === 'race' ? (
                <>
                  <div className="text-xs font-semibold text-gray-900 dark:text-slate-100">Race configuration</div>
                  <p className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">Race configuration JSON file</p>
                  <dl className="mt-3 space-y-2 text-[11px]">
                    {race.status === 'loading' || race.status === 'idle' ? (
                      <LoadingState label="Loading…" className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-slate-400" />
                    ) : race.status === 'error' ? (
                      <div className="text-gray-600 dark:text-slate-400">Could not load metadata (details in notification).</div>
                    ) : race.status === 'empty' ? (
                      <div className="text-gray-600 dark:text-slate-400">
                        No races yet. Add a race in the event overview to store race configuration.
                      </div>
                    ) : race.status === 'ready' ? (
                      <>
                        <div className="flex flex-col gap-0.5">
                          <dt className="font-medium text-gray-500 dark:text-slate-400">Stored file name</dt>
                          <dd className="font-mono text-gray-800 dark:text-slate-200 break-all">
                            {race.fileName || 'race-configuration.json'}
                          </dd>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <dt className="font-medium text-gray-500 dark:text-slate-400">Created</dt>
                          <dd className="text-gray-800 dark:text-slate-200">
                            {formatApiDateTime(race.created_at)}{' '}
                            <span className="text-gray-500 dark:text-slate-400">
                              ·{' '}
                              {formatUserIdentityDisplay(
                                race.created_by,
                                globalUserIdToDisplayName,
                              )}
                            </span>
                          </dd>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <dt className="font-medium text-gray-500 dark:text-slate-400">Last edited</dt>
                          <dd className="text-gray-800 dark:text-slate-200">
                            {formatApiDateTime(race.edited_at)}{' '}
                            <span className="text-gray-500 dark:text-slate-400">
                              ·{' '}
                              {formatUserIdentityDisplay(
                                race.edited_by,
                                globalUserIdToDisplayName,
                              )}
                            </span>
                          </dd>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-500 dark:text-slate-400">—</div>
                    )}
                  </dl>
                </>
              ) : (
                <>
                  <div className="text-xs font-semibold text-gray-900 dark:text-slate-100">Device / athlete assignments</div>
                  <p className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">Athlete assignment CSV file</p>
                  <dl className="mt-3 space-y-2 text-[11px]">
                    {athlete.status === 'loading' || athlete.status === 'idle' ? (
                      <LoadingState label="Loading…" className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-slate-400" />
                    ) : athlete.status === 'error' ? (
                      <div className="text-gray-600 dark:text-slate-400">Could not load metadata (details in notification).</div>
                    ) : athlete.status === 'empty' ? (
                      <div className="text-gray-600 dark:text-slate-400">No athlete assignment file uploaded yet.</div>
                    ) : athlete.status === 'ready' ? (
                      <>
                        <div className="flex flex-col gap-0.5">
                          <dt className="font-medium text-gray-500 dark:text-slate-400">File ID</dt>
                          <dd className="font-mono text-gray-800 dark:text-slate-200 break-all">{athlete.fileId || '—'}</dd>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <dt className="font-medium text-gray-500 dark:text-slate-400">File name</dt>
                          <dd className="font-mono text-gray-800 dark:text-slate-200 break-all">
                            {athlete.fileName || '—'}
                          </dd>
                        </div>
                        {(() => {
                          const t0 = athlete.firstUploadedAt ?? 0;
                          const t1 = athlete.createdAt ?? 0;
                          const u0 = athlete.firstUploadedBy ?? '';
                          const u1 = athlete.createdBy ?? '';
                          const singleVersion =
                            t0 > 0 && t1 > 0 && t0 === t1 && u0 === u1;
                          const lastUploadDd = (
                            <dd className="text-gray-800 dark:text-slate-200">
                              {athlete.createdAt != null && athlete.createdAt > 0
                                ? formatApiDateTime(athlete.createdAt)
                                : '—'}
                              <span className="text-gray-500 dark:text-slate-400">
                                {' '}
                                ·{' '}
                                {athlete.createdByDisplayName?.trim() ||
                                  formatUserIdentityDisplay(
                                    athlete.createdBy ?? '',
                                    globalUserIdToDisplayName,
                                  )}
                              </span>
                            </dd>
                          );
                          if (singleVersion) {
                            return (
                              <div className="flex flex-col gap-0.5">
                                <dt className="font-medium text-gray-500 dark:text-slate-400">Last upload</dt>
                                {lastUploadDd}
                              </div>
                            );
                          }
                          return (
                            <>
                              <div className="flex flex-col gap-0.5">
                                <dt className="font-medium text-gray-500 dark:text-slate-400">Created</dt>
                                <dd className="text-gray-800 dark:text-slate-200">
                                  {athlete.firstUploadedAt != null && athlete.firstUploadedAt > 0
                                    ? formatApiDateTime(athlete.firstUploadedAt)
                                    : '—'}
                                  <span className="text-gray-500 dark:text-slate-400">
                                    {' '}
                                    ·{' '}
                                    {athlete.firstUploadedByDisplayName?.trim() ||
                                      formatUserIdentityDisplay(
                                        athlete.firstUploadedBy ?? '',
                                        globalUserIdToDisplayName,
                                      )}
                                  </span>
                                </dd>
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <dt className="font-medium text-gray-500 dark:text-slate-400">Last upload</dt>
                                {lastUploadDd}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-gray-500 dark:text-slate-400">—</div>
                    )}
                  </dl>
                </>
              )}
            </div>,
          document.body,
        )
      : null;

  const iconBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/80 hover:text-indigo-800';

  return (
    <div className="flex items-center gap-2">
      <button
        ref={raceBtnRef}
        type="button"
        className={iconBtn}
        title="Race configuration file metadata"
        aria-label="Race configuration file metadata"
        aria-expanded={open === 'race'}
        onMouseEnter={openRace}
        onMouseLeave={scheduleClose}
      >
        <FileJson className="h-4 w-4" strokeWidth={2} aria-hidden />
      </button>
      <button
        ref={athleteBtnRef}
        type="button"
        className={iconBtn}
        title="Athlete assignment CSV metadata"
        aria-label="Athlete assignment CSV metadata"
        aria-expanded={open === 'athlete'}
        onMouseEnter={openAthlete}
        onMouseLeave={scheduleClose}
      >
        <Table2 className="h-4 w-4" strokeWidth={2} aria-hidden />
      </button>
      {panel}
    </div>
  );
}
